const ADSENSE_CLIENT = 'ca-pub-8044266231509163';
const ADSENSE_SRC = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
const MIN_INVIEW_RATIO = 0.5;
const MAX_ADSENSE_PUSH_ATTEMPTS = 6;
const ADSENSE_PUSH_RETRY_DELAY_MS = 450;

function loadAdsenseScript() {
  if (window.__wlaAdsenseLoading) return window.__wlaAdsenseLoading;

  window.__wlaAdsenseLoading = new Promise((resolve) => {
    const existing = document.querySelector('script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]');

    if (existing) {
      if (existing.dataset.wlaLoaded === 'true' || window.adsbygoogle?.loaded) {
        existing.dataset.wlaLoaded = 'true';
        resolve();
        return;
      }

      if (existing.dataset.wlaLoaded !== 'true') {
        existing.addEventListener(
          'load',
          () => {
            existing.dataset.wlaLoaded = 'true';
            resolve();
          },
          { once: true }
        );
        existing.addEventListener('error', resolve, { once: true });
        return;
      }

      resolve();
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.src = ADSENSE_SRC;
    script.addEventListener(
      'load',
      () => {
        script.dataset.wlaLoaded = 'true';
        resolve();
      },
      { once: true }
    );
    script.addEventListener('error', resolve, { once: true });
    document.head.appendChild(script);
  });

  return window.__wlaAdsenseLoading;
}

function isVisible(card) {
  return !card.hidden && getComputedStyle(card).display !== 'none';
}

function inViewRatio(card) {
  if (!isVisible(card)) return false;
  const rect = card.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const width = Math.max(0, rect.width);
  const height = Math.max(0, rect.height);
  const area = width * height;

  if (!area) return 0;

  const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
  const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));

  return (visibleWidth * visibleHeight) / area;
}

function isReadyToRequest(card) {
  return inViewRatio(card) >= MIN_INVIEW_RATIO;
}

function slotAlreadyHandled(slot) {
  return (
    slot.children.length > 0 ||
    slot.getAttribute('data-adsbygoogle-status') === 'done' ||
    Boolean(slot.getAttribute('data-ad-status'))
  );
}

function pushAdsenseUntilHandled(card, slot, attempt = 1) {
  if (!slot || slotAlreadyHandled(slot)) return;

  card.dataset.wlaAdPushAttempts = String(attempt);

  try {
    (window.adsbygoogle = window.adsbygoogle || []).push({});
  } catch (error) {
    const message = error && error.message ? error.message : 'AdSense request failed';
    if (!slotAlreadyHandled(slot) && !/All 'ins' elements/.test(message)) {
      card.dataset.wlaAdError = message;
    }
    return;
  }

  if (attempt >= MAX_ADSENSE_PUSH_ATTEMPTS) {
    window.setTimeout(() => {
      if (!slotAlreadyHandled(slot)) {
        card.dataset.wlaAdError = 'AdSense slot not processed after retries';
      }
    }, ADSENSE_PUSH_RETRY_DELAY_MS);
    return;
  }

  window.setTimeout(() => {
    if (!slotAlreadyHandled(slot)) {
      pushAdsenseUntilHandled(card, slot, attempt + 1);
    }
  }, ADSENSE_PUSH_RETRY_DELAY_MS);
}

function requestCard(card, { rescue = false } = {}) {
  if (card.dataset.wlaPlacementDisabled) return;
  if (!isReadyToRequest(card)) return;
  const slot = card.querySelector('ins.adsbygoogle');
  if (!slot || slotAlreadyHandled(slot)) return;
  if (card.dataset.wlaThemeAdRequested === 'true' && !rescue) return;
  if (card.dataset.wlaThemeAdRetried === 'true' && rescue) return;
  if (card.dataset.wlaAdRequested === 'true' && !rescue) return;

  card.dataset.wlaThemeAdRequested = 'true';
  if (rescue) {
    card.dataset.wlaThemeAdRetried = 'true';
  }

  if (window.WLAMonetization?.sendEvent) {
    window.WLAMonetization.sendEvent('ad_requested', card, { source: rescue ? 'theme-loader-rescue' : 'theme-loader' });
  }

  loadAdsenseScript().then(() => {
    pushAdsenseUntilHandled(card, slot);
  });
}

function adCards() {
  return Array.from(
    document.querySelectorAll(
      '.wla-home-ad, .wla-ad-card[data-wla-lazy-ad], .wla-side-rail-ad[data-wla-lazy-ad], [data-price-result-ad], [data-price-rail-ad]'
    )
  );
}

function requestVisibleCards(options = {}) {
  adCards().forEach((card) => requestCard(card, options));
}

export function initAdsenseLoader() {
  const cards = adCards();
  if (!cards.length) return;

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio < MIN_INVIEW_RATIO || !isReadyToRequest(entry.target)) return;
          requestCard(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: [0, MIN_INVIEW_RATIO, 1] }
    );
    cards.forEach((card) => observer.observe(card));
  }

  window.addEventListener('scroll', () => requestVisibleCards(), { passive: true });
  window.addEventListener('resize', () => requestVisibleCards());
  window.addEventListener('wla-price-results-updated', () => requestVisibleCards());
  window.addEventListener('wla-side-rail-updated', () => requestVisibleCards());

  setTimeout(() => requestVisibleCards(), 500);
  setTimeout(() => requestVisibleCards({ rescue: true }), 2500);
  setTimeout(() => requestVisibleCards({ rescue: true }), 8000);
}
